"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";
import { scheduleModalPointerEventsCleanup } from "@/lib/ui/modal-pointer-events";
import { cn } from "@/lib/utils";
import { type AsChildProps, resolveRenderProp } from "./render-prop";

type DialogProps = Omit<
  React.ComponentProps<typeof DialogPrimitive.Root>,
  "children"
> & {
  children?: React.ReactNode;
};

function Dialog({ onOpenChange, open, ...props }: DialogProps) {
  React.useEffect(() => {
    if (open === false) {
      scheduleModalPointerEventsCleanup();
    }
  }, [open]);

  return (
    <DialogPrimitive.Root
      {...props}
      onOpenChange={(nextOpen, eventDetails) => {
        onOpenChange?.(nextOpen, eventDetails);
        if (!nextOpen) {
          scheduleModalPointerEventsCleanup();
        }
      }}
      open={open}
    />
  );
}

function DialogTrigger({
  asChild = false,
  children,
  nativeButton,
  render,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger> & AsChildProps) {
  const renderProps = resolveRenderProp({
    asChild,
    children,
    nativeButton,
    render,
  });

  return (
    <DialogPrimitive.Trigger
      data-slot="dialog-trigger"
      nativeButton={renderProps.nativeButton}
      render={renderProps.render}
      {...props}
    >
      {renderProps.children}
    </DialogPrimitive.Trigger>
  );
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  asChild = false,
  children,
  nativeButton,
  render,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close> & AsChildProps) {
  const renderProps = resolveRenderProp({
    asChild,
    children,
    nativeButton,
    render,
  });

  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      nativeButton={renderProps.nativeButton}
      render={renderProps.render}
      {...props}
    >
      {renderProps.children}
    </DialogPrimitive.Close>
  );
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        "pointer-events-auto fixed inset-0 z-1000 bg-black/60 opacity-100 backdrop-blur-md transition-opacity data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="dialog-overlay"
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  layer,
  showCloseButton = true,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Popup> & {
  /** Base z-index for the backdrop; dialog content renders one layer above. */
  layer?: number;
  showCloseButton?: boolean;
}) {
  const describedByProps =
    "aria-describedby" in props
      ? {}
      : ({ "aria-describedby": undefined } as const);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay
        style={layer === undefined ? undefined : { zIndex: layer }}
      />
      <DialogPrimitive.Popup
        className={cn(
          "pointer-events-auto fixed top-[50%] left-[50%] z-1000 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] scale-100 gap-4 rounded-2xl border border-black/15 bg-black/80 p-6 text-text-primary opacity-100 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6),inset_0_1px_1.5px_rgba(255,255,255,0.15)] backdrop-blur-xl transition-[opacity,transform] duration-200 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 sm:max-w-lg dark:border-white/10 dark:bg-black/90",
          className,
        )}
        data-slot="dialog-content"
        style={{
          ...style,
          ...(layer === undefined ? {} : { zIndex: layer + 1 }),
        }}
        {...describedByProps}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogClose className="absolute top-4 right-4 flex items-center justify-center rounded-full border border-white/5 bg-white/5 p-1.5 opacity-60 transition-[color,background-color,opacity,transform] duration-150 ease-out-strong hover:bg-white/10 hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden active:scale-95 disabled:pointer-events-none dark:hover:bg-white/10 pointer-fine:hover:scale-105 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      data-slot="dialog-header"
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      data-slot="dialog-footer"
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-lg leading-none font-semibold", className)}
      data-slot="dialog-title"
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
